import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

const LOGIN_KEY = 'login';
const USER_KEY = 'user';
const ACCESS_TOKEN = 'token';
const REFRESH_TOKEN = 'refreshToken';
const SESSION_ID = 'sessionId';
const USER_ID = 'userId';

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {
  constructor(private router: Router) {
    this.handleLogoutParam();
  }

  // 🔐 Clear all local authentication storage without page reload
  public clearSession(): void {
    localStorage.removeItem(LOGIN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(SESSION_ID);
    localStorage.removeItem(USER_ID);
    localStorage.removeItem('userProfileImage');
  }

  // 🔐 Logout and clear all local storage
  signOut(): void {
    this.clearSession();
    window.location.reload();
  }

  // 🔐 Check URL for logout query param (?logout=1) and clear session if present
  public handleLogoutParam(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      var searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('logout') === '1' || searchParams.has('logout')) {
        console.log('[Auth] Logout parameter detected. Clearing local session.');
        this.clearSession();
        searchParams.delete('logout');
        var newSearch = searchParams.toString() ? '?' + searchParams.toString() : '';
        var newUrl = window.location.pathname + newSearch + window.location.hash;
        window.history.replaceState(null, '', newUrl);
        return true;
      }
    } catch (e) {
      console.error('[Auth] Error handling logout query param:', e);
    }
    return false;
  }

  public isLoggedIn(): boolean {
    return this.getIsUserLogin() === 'true' && !!this.getAccessToken();
  }

  // ✅ Save login state
  public saveIsUserLogin(login: string): void {
    localStorage.setItem(LOGIN_KEY, login);
  }

  // ✅ Get login state
  public getIsUserLogin(): string {
    return localStorage.getItem(LOGIN_KEY) || 'false';
  }


  // ✅ Save profile image URL
  saveProfileImage(url: string): void {
    localStorage.setItem('userProfileImage', url);
  }

  getProfileImage(): string | null {
    return localStorage.getItem('userProfileImage');
  }

  // ✅ Save user info
  public saveUser(user: any): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  // ✅ Get user info
  public getUser(): any {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  removeUser(): void {
    localStorage.removeItem(USER_KEY);
  }
  // ✅ Get token
  public getAccessToken(): string {
    return localStorage.getItem(ACCESS_TOKEN) || '';
  }

  // ✅ Save token
  public saveAccessToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN, token);
    console.log('Access token saved:', token);
  }

  public saveRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN, token);
    console.log('Refresh token saved:', token);
  }

  public getRefreshToken(): string {
    return localStorage.getItem(REFRESH_TOKEN) || '';
  }

  public saveSessionId(sessionId: string): void {
    localStorage.setItem(SESSION_ID, sessionId);
    console.log('Session ID saved:', sessionId);
  }

  public getSessionId(): string {
    return localStorage.getItem(SESSION_ID) || '';
  }

  public saveUserId(userId: string): void {
    localStorage.setItem(USER_ID, userId);
    console.log('User ID saved:', userId);
  }

  public getUserId(): string {
    return localStorage.getItem(USER_ID) || '';
  }

  public persistOAuthSession(response: any): void {
    if (response && response.access_token) {
      this.saveAccessToken(response.access_token);
      var userId = this.getJwtSubject(response.access_token);
      if (userId) {
        this.saveUserId(userId);
      }
    }

    if (response && response.refresh_token) {
      this.saveRefreshToken(response.refresh_token);
    }

    if (response && response.session_id) {
      this.saveSessionId(response.session_id);
    }

    this.saveIsUserLogin('true');
  }

  public persistOAuthUserInfo(profile: any): void {
    var userId = profile && (profile.sub || profile.id || profile._id) || '';
    var displayName = profile && (profile.name || profile.fullName || profile.fullname || profile.username || profile.preferred_username) || '';
    var user = {
      id: userId,
      _id: userId,
      userId: userId,
      fullName: displayName,
      fullname: displayName,
      name: displayName,
      username: profile && (profile.preferred_username || profile.username || displayName) || '',
      email: profile && (profile.email || profile.mail) || '',
      mail: profile && (profile.email || profile.mail) || '',
      profile: profile && (profile.picture || profile.profile || profile.image || profile.avatar) || '',
      image: profile && (profile.picture || profile.profile || profile.image || profile.avatar) || '',
      avatar: profile && (profile.picture || profile.profile || profile.image || profile.avatar) || '',
      provider: profile && profile.provider || '',
      phone: profile && (profile.phone_number || profile.phone) || '',
      sector: profile && profile.sector || '',
      address: profile && profile.address || ''
    };

    if (userId) {
      this.saveUserId(userId);
    }

    this.saveUser(user);
    this.saveIsUserLogin('true');
  }

  // ✅ Check if token exists
  public hasToken(): boolean {
    return !!this.getAccessToken();
  }

  private getJwtSubject(token: string): string {
    try {
      var payload = token.split('.')[1];
      var normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      var decoded = JSON.parse(atob(normalized));
      return decoded.sub || '';
    } catch (error) {
      return '';
    }
  }
}
